import { useEffect, useMemo, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'
import { CellValueChangedEvent } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { Check } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { AgGridWidget, CustomMenuItem, createContextMenu } from '@fortem-monorepo/react-ag'
import { useApi, useUserContext } from '@fortem-monorepo/react-spa'
import { Audit, Trades } from '@fortem-monorepo/web-sdk'

import { useChangeActiveTradeMenuItem } from '@/ag-grid/context-menus/activate-trade'
import { createBespokeColDefs } from '@/pages/trading/booked-trades/grids/bespoke/coldefs'
import { useApproveElementMutation } from '@/react-query/mutations/audit'
import { useUpdateBespokeTradeMutation } from '@/react-query/mutations/trades'
import { useInternalUser } from '@/react-query/queries/account'
import { useTradingData } from '@/react-query/queries/static'

export interface BespokeTradesGridProps {
  params: Trades.GetTradesParams
  source: 'Manual' | 'Smartrade' | 'all'
}

export const BespokeTradesGrid: React.FC<BespokeTradesGridProps> = ({ source, params }) => {
  const { internalUser } = useInternalUser()
  const userContext = useUserContext()

  const api = useApi()

  const query = useQuery({
    queryKey: ['trades', 'bespoke', params],
    queryFn: () => api.trades.getBespokeTrades(params),
  })

  const tradeData = useTradingData()
  const updateBespokeTradeMutation = useUpdateBespokeTradeMutation()
  const approveElementMutation = useApproveElementMutation()

  const gridRef = useRef<AgGridReact>(null)

  const colDefs = useMemo(
    () => createBespokeColDefs({ ctx: userContext, tradeData }),
    [userContext, tradeData],
  )

  useEffect(() => {
    if (gridRef.current?.api) {
      if (source === 'all') {
        gridRef.current?.api.setFilterModel({
          trade_source: null,
        })
      } else {
        gridRef.current?.api.setFilterModel({
          trade_source: {
            filterType: 'set',
            values: [String(source)],
          },
        })
      }
    }
  }, [source])

  const { Dialog: ChangeActiveTradeModal, createMenuItems } =
    useChangeActiveTradeMenuItem<Trades.BespokeTrade>({
      mutation: updateBespokeTradeMutation,
    })

  const createBespokeContextMenu = () => {
    return createContextMenu<Trades.BespokeTrade>((params) => {
      const status = params.node?.data?.status
      const element_id = params.node?.data?.trade_id!
      const statusIsNotApproved = status !== Audit.STATUSES.APPROVED
      const lastUpdatedByUser = internalUser?.user_id == params.node?.data?.submitted_by

      const canApprove = statusIsNotApproved && !lastUpdatedByUser

      return [
        ...(canApprove
          ? ([
              {
                name: 'Approve Trade',
                icon: renderToString(<Check size={16} />),
                cssClasses: ['hover:bg-green-100!', 'text-green-600!'],
                action: () => {
                  return approveElementMutation.mutateAsync({
                    element_id,
                    element_type: Audit.ELEMENT_TYPES.TRADE,
                  })
                },
              },
              'separator',
            ] satisfies CustomMenuItem[])
          : []),
        ...createMenuItems(params),
        'copy',
        'copyWithHeaders',
        'copyWithGroupHeaders',
        'separator',
        'export',
      ]
    })
  }

  return (
    <>
      <ChangeActiveTradeModal />
      <AgGridWidget
        widget={{ exposed: true, className: 'h-full' }}
        widgetHeader={{ title: 'Bespoke Trades' }}
        query={query}
        grid={{
          defaultCsvExportParams: {
            allColumns: true,
          },
          ref: gridRef,
          getContextMenuItems: createBespokeContextMenu(),
          domLayout: 'normal',
          rowData: query.data,
          columnDefs: colDefs,
          autoSizeStrategy: { type: 'fitCellContents' },
          onCellValueChanged: (params: CellValueChangedEvent<Trades.BespokeTrade>) => {
            return updateBespokeTradeMutation.mutateAsync({
              rowData: params.data,
              updatedField: params.colDef.field,
              oldValue: params.oldValue,
              newValue: params.newValue,
            })
          },
        }}
      />
    </>
  )
}
